import { paymentProviderZodSchema } from "@@/shared/payments/payment-providers";
import { priceZodSchema } from "@@/shared/prices/price";
import { translatableZodSchema } from "@@/shared/translations/translation";
import { userIdZodSchema } from "@@/shared/users/user-id";
import type { UUID } from "@@/shared/uuid";
import type { Except } from "type-fest";
import { z } from "zod";
import { signatureFactory } from "../../signature-factory";
import type {
    _OrderEventBaseFactoryParams} from "../_order-base.event";
import {
    _orderEventBaseFactory,
    _orderEventBaseZodObject,
} from "../_order-base.event";
import { orderEventIdZodSchema } from "../_order-event-id";

const orderPaymentRefundFulfilledEventZodObject =
    _orderEventBaseZodObject.merge(
        z.object({
            type: z.literal("PAYMENT_REFUND_FULFILLED"),
            createdBy: userIdZodSchema.or(z.string()),
            refundedAmount: priceZodSchema, //z.number().min(0),
            //requestedRefundAmount: z.number().min(0),
            paymentUUID: z.string() as unknown as z.ZodType<UUID>,
            refundUUID: z.string().optional() as unknown as z.ZodType<
                UUID | undefined
            >,
            paymentProvider: paymentProviderZodSchema,
            reason: translatableZodSchema.optional(),
            paymentFor: z.object({
                orderEventId: orderEventIdZodSchema,
                /** OriginEvent is either a change event or a created event */
                originEventId: orderEventIdZodSchema.optional(),
            }),
            legacyServiceFee: z.boolean(),
            /**
             * If a percentage is set, it means that the refund is a divided
             * refund the refund has been divided between multiple previous
             * payments e.g. an order has been payed twice, once with swish
             * and once with stripe and the refund amount is larger than
             * either one of them then we divide the refund between the two
             * previous payments and repay part using swish and part using
             * stripe the percentage is the percentage of the requested refund
             * that is this is being refunded by this initiation
             */
            percentage: z.number().optional(),
        }),
    );
type OrderPaymentRefundFulfilledEvent = z.infer<
    typeof orderPaymentRefundFulfilledEventZodObject
>;
const orderPaymentRefundFulfilledEventZodSchema =
    orderPaymentRefundFulfilledEventZodObject as z.ZodSchema<OrderPaymentRefundFulfilledEvent>;

type FactoryParams = _OrderEventBaseFactoryParams &
    Except<
        OrderPaymentRefundFulfilledEvent,
        "type" | "_id" | "_type" | "_createdAt" | "signature"
    >;

type Params = Except<FactoryParams, "signature">;

const orderPaymentRefundFulfilledEventFactory = (
    params: Params,
): OrderPaymentRefundFulfilledEvent => ({
    ..._orderEventBaseFactory({
        ...params,
        signature: signatureFactory(
            params.orderId,
            "PAYMENT_REFUND_FULFILLED",
            [params.refundUUID ?? ""],
        ),
    }),

    type: "PAYMENT_REFUND_FULFILLED",
    createdBy: params.createdBy,
    refundedAmount: params.refundedAmount,
    //requestedRefundAmount: params.requestedRefundAmount,
    paymentUUID: params.paymentUUID,
    paymentProvider: params.paymentProvider,
    refundUUID: params.refundUUID,
    reason: params.reason,
    paymentFor: params.paymentFor,
    legacyServiceFee: params.legacyServiceFee,
});

const isOrderPaymentRefundFulfilledEvent = (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    event: any,
): event is OrderPaymentRefundFulfilledEvent => {
    return (
        event._type === "ORDER_EVENT" &&
        event?.type === "PAYMENT_REFUND_FULFILLED"
    );
};

export {
    isOrderPaymentRefundFulfilledEvent,
    orderPaymentRefundFulfilledEventFactory,
    orderPaymentRefundFulfilledEventZodObject,
    orderPaymentRefundFulfilledEventZodSchema,
};
export type { OrderPaymentRefundFulfilledEvent };
